<template>
  <div v-if="day_report && !isLoading">
    <!-- Mobile View -->
    <v-card v-if="isMobile" class="content-card mobile-card">
      <v-tabs
        v-model="tabs"
        bg-color="primary-lighten-4"
        slider-color="primary-darken-2"
        grow
        class="tab-nav"
        density="compact"
      >
        <!-- Day report lines tab -->
        <v-tab value="tab-1" class="tab-icon">
          <v-icon>mdi-clipboard-text</v-icon>
        </v-tab>

        <!-- DGs tab -->
        <v-tab v-if="hasAccounts" value="tab-2" class="tab-icon">
          <v-icon>mdi-account-group</v-icon>
        </v-tab>

        <!-- Comments tab -->
        <v-tab value="tab-4" class="tab-icon">
          <v-icon>mdi-comment-multiple</v-icon>
        </v-tab>

        <!-- Attachments tab -->
        <v-tab value="tab-3" class="tab-icon">
          <v-icon>mdi-paperclip</v-icon>
        </v-tab>
      </v-tabs>

      <v-window v-model="tabs">
        <!-- Day report lines tab content -->
        <v-window-item value="tab-1">
          <div class="pa-1">
            <DayReportContent
              :day_report="day_report"
              :current_active="false"
              :can-edit="day_report.can_edit"
            />
          </div>
        </v-window-item>

        <!-- DGs tab content -->
        <v-window-item v-if="hasAccounts" value="tab-2">
          <div class="pa-1">
            <AccountBox
              v-for="account in accounts"
              :key="account.id"
              :account="account"
              :day_report="day_report"
              class="mb-2"
            />
          </div>
        </v-window-item>

        <!-- Attachments tab content -->
        <v-window-item value="tab-3">
          <div class="pa-1">
            <!-- Attachments -->
            <AttachmentsBox
              record-type="DayReport"
              :record-id="day_report.id"
              :item="day_report"
            />
          </div>
        </v-window-item>

        <!-- Comments tab content -->
        <v-window-item value="tab-4">
          <div class="pa-1">
            <!-- Comments -->
            <CommentBox :id="day_report.id" type="DayReport" />
          </div>
        </v-window-item>
      </v-window>
    </v-card>

    <!-- Desktop View -->
    <v-container v-else class="pa-0">
      <v-row>
        <v-col cols="12" lg="7">
          <!-- Day Report Content -->
          <DayReportContent
            :day_report="day_report"
            :current_active="false"
            :can-edit="day_report.can_edit"
            class="mb-4"
          />

          <!-- Accounts Section -->
          <v-card v-if="hasAccounts" class="content-card">
            <v-card-title class="bg-blue-lighten-5">
              <v-icon start color="blue-darken-1" class="mr-2"
                >mdi-account-group</v-icon
              >
              DGs
            </v-card-title>
            <v-card-text>
              <AccountBox
                v-for="account in accounts"
                :key="account.id"
                :account="account"
                :day_report="day_report"
                class="mb-3"
              />
            </v-card-text>
          </v-card>
        </v-col>

        <v-col cols="12" lg="5">
          <!-- Attachments -->
          <AttachmentsBox
            record-type="DayReport"
            :record-id="day_report.id"
            :item="day_report"
            class="mb-4 content-card"
          />

          <!-- Comments -->
          <CommentBox
            :id="day_report.id"
            type="DayReport"
            class="content-card"
          />
        </v-col>
      </v-row>
    </v-container>
  </div>
  <div
    v-else-if="isLoading"
    class="d-flex justify-center align-center"
    style="min-height: 300px"
  >
    <v-progress-circular
      indeterminate
      color="primary"
      size="64"
    ></v-progress-circular>
  </div>
  <v-alert
    v-else
    type="error"
    variant="tonal"
    class="mt-5"
    icon="mdi-alert-circle"
  >
    Dagrapport niet gevonden of er is een fout opgetreden bij het laden.
  </v-alert>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, watch, nextTick } from "vue";
import { useRoute } from "vue-router";
import DayReportContent from "./DayReportContent.vue";
import AccountBox from "../accounts/AccountBox.vue";
import AttachmentsBox from "../attachments/AttachmentsBox.vue";
import CommentBox from "../comments/CommentBox.vue";
import { useMenuStore } from "@/stores/menuStore";
import { useDayReportsStoreRefactor } from "@/stores/dayReportsStoreRefactor";
import { useAccountsStoreRefactor } from "@/stores/accountsStoreRefactor";
import { useReportsStoreRefactor } from "@/stores/reportsStoreRefactor";
import { useDashboardCoordinationStore } from "@/stores/dashboardCoordinationStore";
import { useContextMenuStore } from "@/stores/contextMenuStore";
import { useAlertStore } from "@/stores/alertStore";
import { useDayReportRefresh } from "@/composables/useDayReportRefresh";

function getUnique(arr, comp) {
  if (!arr || !Array.isArray(arr)) return [];

  const unique = arr
    .map((e) => e?.[comp])
    .map((e, i, final) => final.indexOf(e) === i && i)
    .filter((e) => arr[e])
    .map((e) => arr[e]);

  return unique;
}

// Store instances
const menuStore = useMenuStore();
const dayReportsStore = useDayReportsStoreRefactor();
const accountsStore = useAccountsStoreRefactor();
const reportsStore = useReportsStoreRefactor();
const dashboardCoordinationStore = useDashboardCoordinationStore();
const contextMenuStore = useContextMenuStore();
const alertStore = useAlertStore();
const route = useRoute();

// Reactive state
const isMobile = ref(false);
const tabs = ref("tab-1");
const isLoading = ref(true);
const loadError = ref(false);

// Map URL tab parameter to actual tab values
const tabMapping = {
  content: "tab-1",
  accounts: "tab-2",
  attachments: "tab-3",
  comments: "tab-4",
};

// Computed properties
const day_report = computed(() => {
  if (!route.params.id) return null;

  const id = Number(route.params.id);
  if (isNaN(id)) return null;

  const report = dayReportsStore.getDayReportById(id);

  // Debug logging
  console.log("DayReportSelected - day_report:", report);
  if (report) {
    console.log("DayReportSelected - accounts:", report.accounts);
    console.log("DayReportSelected - reports:", report.reports);
  }

  return report;
});

// Get accounts that have reports for this day_report using coordinated data
const accounts = computed(() => {
  if (!day_report.value?.id) return [];

  // Get all reports for this day_report from the coordinated data
  const dayReportReports = reportsStore.reports.filter(
    (report) =>
      report.day_report_id == day_report.value.id ||
      String(report.day_report_id) === String(day_report.value.id),
  );

  // Get unique account IDs from these reports
  const accountIds = [
    ...new Set(
      dayReportReports.map((report) => report.account_id).filter(Boolean),
    ),
  ];

  // Get the actual account objects from the store
  const accountsFromStore = accountIds
    .map((accountId) => accountsStore.getById(accountId))
    .filter(Boolean); // Remove any null accounts

  console.log(
    `DayReportSelected: Found ${accountsFromStore.length} accounts with reports for day_report ${day_report.value.id}`,
  );

  // Fallback: if no accounts in store but we have embedded accounts, use those
  if (
    accountsFromStore.length === 0 &&
    day_report.value?.accounts?.length > 0
  ) {
    console.log("DayReportSelected: Falling back to embedded accounts array");
    return getUnique(day_report.value.accounts, "id");
  }

  return accountsFromStore;
});

const hasAccounts = computed(() => {
  return accounts.value && accounts.value.length > 0;
});

// Use the day report refresh composable
const { refreshDayReport } = useDayReportRefresh(day_report);

// Methods
function onResize() {
  isMobile.value = window.innerWidth < 992;
}

// Handle tab switching based on URL
function handleTabFromUrl() {
  console.log("DayReportSelected: Checking for tab parameter in URL");
  const tabParam = route.query.tab;

  if (tabParam && tabMapping[tabParam]) {
    console.log(
      `DayReportSelected: Setting tab to ${tabMapping[tabParam]} from URL parameter ${tabParam}`,
    );
    tabs.value = tabMapping[tabParam];
  }
}

// Handle comment highlighting
async function handleCommentHighlight() {
  console.log("DayReportSelected: Checking for comment hash in URL");

  // Check for comment hash (allow both lowercase c and uppercase C)
  const hash = route.hash.toLowerCase();
  if (hash && (hash.startsWith("#c") || hash.startsWith("#c"))) {
    console.log("DayReportSelected: Found comment hash:", route.hash);

    // Switch to comments tab
    tabs.value = "tab-4";

    // Wait for DOM to update
    await nextTick();

    // Extract comment ID
    const commentId = hash.substring(2);
    console.log("DayReportSelected: Comment ID from hash:", commentId);

    // Try multiple times with increasing delays to find the comment element
    const findAndHighlightComment = async (attempts = 0) => {
      if (attempts > 5) {
        console.log(
          "DayReportSelected: Max attempts reached, giving up on finding comment",
        );
        return;
      }

      const delay = 200 * Math.pow(1.5, attempts);
      console.log(
        `DayReportSelected: Attempt ${attempts + 1}, waiting ${delay}ms`,
      );

      await new Promise((resolve) => setTimeout(resolve, delay));

      // Try both lowercase and uppercase IDs
      let commentElement = document.getElementById(`c${commentId}`);
      if (!commentElement) {
        commentElement = document.getElementById(`C${commentId}`);
      }

      if (commentElement) {
        console.log(
          "DayReportSelected: Comment element found, highlighting it",
        );

        // Scroll the comment into view
        commentElement.scrollIntoView({ behavior: "smooth", block: "center" });

        // Add highlight animation
        commentElement.classList.add("highlight-comment");
        console.log("DayReportSelected: Added highlight-comment class");

        // Remove highlight after animation
        setTimeout(() => {
          commentElement.classList.remove("highlight-comment");
          console.log("DayReportSelected: Removed highlight-comment class");
        }, 2000);
      } else {
        console.log(
          `DayReportSelected: Comment element not found, trying again (attempt ${attempts + 1})`,
        );
        findAndHighlightComment(attempts + 1);
      }
    };

    // Start the finding process
    findAndHighlightComment();
  }
}

// Watch for route changes
watch(() => route.query.tab, handleTabFromUrl);
watch(() => route.hash, handleCommentHighlight);

// Watch for day report ID changes and coordinate dashboard loading
watch(
  () => route.params.id,
  async (newId, oldId) => {
    console.log(
      `DayReportSelected: Day report ID changed from ${oldId} to ${newId}`,
    );

    if (newId && newId !== oldId) {
      try {
        // Reset loading state
        isLoading.value = true;
        loadError.value = false;

        const dayReportId = Number(newId);
        console.log(
          `DayReportSelected: Coordinating dashboard load for day report ${dayReportId}`,
        );

        // Use dashboard coordination to load all related data
        await dashboardCoordinationStore.coordinateDashboardLoad(
          dayReportId,
          accountsStore,
          reportsStore,
          dayReportsStore,
        );

        // Check if the day report was found
        if (!day_report.value) {
          console.error(
            `DayReportSelected: Day report with ID ${newId} not found after coordination`,
          );
          loadError.value = true;
          alertStore.error("Dagrapport niet gevonden");
        } else {
          console.log(
            `DayReportSelected: Successfully coordinated dashboard load for day report ${dayReportId}`,
          );

          // Process URL parameters after data is loaded
          handleTabFromUrl();
          handleCommentHighlight();
        }
      } catch (error) {
        console.error(
          `DayReportSelected: Error during dashboard coordination for day report ${newId}:`,
          error,
        );
        loadError.value = true;
        alertStore.error("Probleem bij het laden van het dagrapport");
      } finally {
        isLoading.value = false;
      }
    }
  },
  { immediate: true },
);

// Context menu definition
const context_menu = [
  {
    name: "Vernieuwen",
    action: refreshDayReport,
    icon: "mdi-refresh",
  },
];

// Lifecycle hooks
onMounted(async () => {
  console.log("DayReportSelected: Component mounted");
  console.log("DayReportSelected: Adding context menu options:", context_menu);

  // Show the menu
  menuStore.setMenuVisible(true);

  // Use nextTick to ensure the context menu is set after any route-based resets
  await nextTick();
  console.log(
    "DayReportSelected: Context menu store options before adding:",
    contextMenuStore.options,
  );
  contextMenuStore.addOption(context_menu);
  console.log(
    "DayReportSelected: Context menu store options after adding:",
    contextMenuStore.options,
  );

  // Setup resize listener
  onResize();
  window.addEventListener("resize", onResize);
});

onUnmounted(() => {
  console.log("DayReportSelected: Component unmounted, clearing context menu");
  window.removeEventListener("resize", onResize);
  contextMenuStore.reset(); // Clear context menu when component unmounts
});
</script>

<style scoped>
.content-card {
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.mobile-card {
  border-radius: 0;
  padding: 0;
  margin: 0;
}

.v-window {
  background-color: white;
}

.v-window-item {
  padding: 0;
}

.v-window-item > div {
  margin-top: 12px;
}

/* Comment highlight animation */
:deep(.highlight-comment) {
  animation: highlight-pulse 2s ease !important;
  background-color: rgba(
    255,
    193,
    7,
    0.3
  ) !important; /* Using amber color directly */
  border-radius: 8px !important;
  box-shadow: 0 0 0 4px rgba(255, 193, 7, 0.2) !important;
  position: relative !important;
  z-index: 2 !important;
}

@keyframes highlight-pulse {
  0% {
    background-color: rgba(255, 193, 7, 0.3) !important;
    transform: scale(1) !important;
  }
  50% {
    background-color: rgba(255, 193, 7, 0.5) !important;
    transform: scale(1.02) !important;
  }
  100% {
    background-color: rgba(255, 193, 7, 0.3) !important;
    transform: scale(1) !important;
  }
}

/* Tab navigation styling */
.tab-nav {
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  position: sticky;
  top: 0;
  z-index: 10;
}

/* Icon-only tab styling */
.tab-icon {
  min-width: 0 !important;
  width: auto !important;
  padding: 0 !important;
  opacity: 0.8;
  transition: opacity 0.2s ease;
}

.tab-icon:hover,
.tab-icon.v-tab--selected {
  opacity: 1;
}

.tab-icon .v-icon {
  margin: 0 !important;
  font-size: 24px !important;
}

/* Make sure the tabs take up equal space */
.v-tabs--density-compact {
  height: 56px;
}

.v-tabs--density-compact .v-tab {
  height: 100%;
}
</style>
